import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { createContext, ReactNode, useContext, useEffect } from 'react';
import { SentenceType } from '../../../../server/agent/learningContent/learningContentTypes';
import localStorageUtil, { LocalStorageItem } from '../../../clientUtils/localStorageUtil';
import { useMobxInstance } from '../../../clientUtils/mobxUtil';

export type TextVisibility = 'hidden' | 'hint' | 'visible';
export type LessonControlOption = LocalStorageItem['sentenceControlOption']['value'];

const DEFAULT_CONTROL_OPTION: LessonControlOption = {
    textAlwaysVisible: false,
    translationAlwaysVisible: false,
};

/**
 * 문장 학습 UI 상태 관리
 * - 현재 문장 데이터
 * - 텍스트 표시 (3단계: hidden, hint, visible)
 * - 번역 표시/숨김
 * - 가이드 팝업 상태
 * - 컨트롤 옵션 (항상 보기 모드 등, 로컬 스토리지에 저장)
 *
 * 주의: sentence가 변경되면 새 인스턴스가 생성되어 상태가 초기화됨
 * 단, controlOption은 로컬 스토리지에서 로드되어 유지됨
 */
class SentenceStore {
    readonly sentence: SentenceType | null;
    /**
     * 학습노트가 있는 청크의 인덱스 배열
     */
    readonly learningNoteChunkIndexes: number[];

    /**
     * 원어 텍스트 표시 상태
     */
    textVisibility: TextVisibility = 'hidden';
    /**
     * 번역 표시 상태
     */
    isTranslationVisible = false;
    /**
     * 현재 보여주는 학습노트의 청크 인덱스 (-1이면 숨김)
     */
    currentLearningNoteChunkIndex = -1;
    /**
     * 컨트롤 옵션 (로컬 스토리지에 저장)
     */
    controlOption: LessonControlOption = { ...DEFAULT_CONTROL_OPTION };

    openedVocalGuideIndex: number | null = null;

    constructor(sentence: SentenceType | null) {
        makeAutoObservable(this);

        this.sentence = sentence;

        // 학습 노트가 있는 청크 인덱스들을 미리 계산
        this.learningNoteChunkIndexes =
            sentence?.chunks
                .map((chunk, index) => (chunk.learningNote ? index : -1))
                .filter((index) => index >= 0) ?? [];
    }

    /**
     * 학습노트가 존재하는지 여부
     */
    get hasLearningNotes(): boolean {
        return this.learningNoteChunkIndexes.length > 0;
    }

    /**
     * 현재 선택된 학습노트 내용
     */
    get currentLearningNote(): string | null {
        if (this.currentLearningNoteChunkIndex < 0) {
            return null;
        }
        return this.sentence?.chunks[this.currentLearningNoteChunkIndex]?.learningNote ?? null;
    }

    loadControlOption() {
        const savedOption = localStorageUtil.getItem({ key: 'sentenceControlOption' });
        this.controlOption = { ...DEFAULT_CONTROL_OPTION, ...savedOption };

        // 항상 보기 모드면 초기값을 visible로 설정
        if (this.controlOption.textAlwaysVisible) {
            this.textVisibility = 'visible';
        }
        if (this.controlOption.translationAlwaysVisible) {
            this.isTranslationVisible = true;
        }
    }

    private saveControlOption() {
        localStorageUtil.setItem({
            key: 'sentenceControlOption',
            value: this.controlOption,
        });
    }

    // 텍스트 표시 단계 순환: hidden → hint → visible → hidden
    cycleTextVisibility() {
        const cycle: TextVisibility[] = ['hidden', 'hint', 'visible'];
        const currentIndex = cycle.indexOf(this.textVisibility);
        const nextIndex = (currentIndex + 1) % cycle.length;

        this.setTextVisibility(cycle[nextIndex]);
    }

    setTextVisibility(visibility: TextVisibility) {
        this.textVisibility = visibility;
        // 텍스트를 숨기면 가이드도 숨김
        if (this.textVisibility !== 'visible') {
            this.openedVocalGuideIndex = null;
        }
    }

    setTextAlwaysVisible(value: boolean) {
        this.controlOption.textAlwaysVisible = value;
        this.saveControlOption();
        if (value) {
            this.textVisibility = 'visible';
        } else {
            this.textVisibility = 'hidden';
        }
    }

    /**
     * 번역 표시/숨기기
     */
    toggleTranslationVisible() {
        this.isTranslationVisible = !this.isTranslationVisible;

        // 항상 보기 옵션이 켜져있었다면, 번역을 숨기면 같이 꺼준다
        if (this.controlOption.translationAlwaysVisible && !this.isTranslationVisible) {
            this.setTranslationAlwaysVisible(false);
        }
    }

    setTranslationAlwaysVisible(value: boolean) {
        this.controlOption.translationAlwaysVisible = value;
        this.saveControlOption();
        this.isTranslationVisible = value;
    }

    /**
     * 학습노트 표시 순환
     * - 숨겨진 상태 → 첫 번째 노트 표시
     * - 표시 중 → 다음 노트로 전환
     * - 마지막 노트 → 숨김
     */
    toggleLearningNoteVisible() {
        if (!this.hasLearningNotes) {
            return;
        }

        // 현재 인덱스의 배열 위치 찾기
        const currentArrayIndex = this.learningNoteChunkIndexes.indexOf(
            this.currentLearningNoteChunkIndex,
        );
        const nextArrayIndex = currentArrayIndex + 1;

        if (nextArrayIndex < this.learningNoteChunkIndexes.length) {
            this.currentLearningNoteChunkIndex = this.learningNoteChunkIndexes[nextArrayIndex];
        } else {
            // 마지막 노트였으면 숨김
            this.currentLearningNoteChunkIndex = -1;
        }
    }

    /**
     * 현재 학습노트 청크 인덱스 변경
     */
    setCurrentLearningNoteChunkIndex(chunkIndex: number) {
        if (this.learningNoteChunkIndexes.includes(chunkIndex)) {
            this.currentLearningNoteChunkIndex = chunkIndex;
        }
    }

    toggleVocalGuide(chunkIndex: number) {
        // 같은 가이드를 클릭하면 닫기, 다른 가이드를 클릭하면 전환
        this.openedVocalGuideIndex = this.openedVocalGuideIndex === chunkIndex ? null : chunkIndex;
    }

    isVocalGuideOpen(chunkIndex: number): boolean {
        return this.openedVocalGuideIndex === chunkIndex;
    }
}

const SentenceContext = createContext<SentenceStore | null>(null);

export const SentenceProvider = observer(
    ({ sentence, children }: { sentence: SentenceType | null; children: ReactNode }) => {
        const sentenceStore = useMobxInstance(SentenceStore, sentence);

        useEffect(() => {
            sentenceStore.loadControlOption();
        }, [sentenceStore]);

        return (
            <SentenceContext.Provider value={sentenceStore}>{children}</SentenceContext.Provider>
        );
    },
);

/**
 * 문장 학습 상태 스토어 사용 훅
 */
export const useSentenceStore = (): SentenceStore => {
    const sentenceStore = useContext(SentenceContext);

    if (!sentenceStore) {
        throw new Error('useSentenceStore must be used within a SentenceProvider');
    }

    return sentenceStore;
};
